import React, {
  useEffect,
  useRef,
  FunctionComponent,
  KeyboardEvent,
  LiHTMLAttributes,
  SyntheticEvent,
} from "react";
import classnames from 'classnames';
import 'intersection-observer';

import { BaseDropdownItemProps } from '@components/library/dropdown_item';
import { keyPressHelper } from '@helpers/accessibility';

const baseName = 'dropdown-item';

const BaseDropdownItem: FunctionComponent<BaseDropdownItemProps & LiHTMLAttributes<HTMLLIElement>> = (
  props
) => {
  const {
    children,
    className,
    icon = '',
    iconSize,
    id,
    isArrowed,
    isDisabled = false,
    isDisplayed = true,
    isSelected,
    isTabbable = true,
    isWarningItem = false,
    name,
    onChoose,
    styleClassNames = {}, // default value for testing (required prop)
    variantName = '', // default value for testing (required prop)
    ...liProps
  } = props;

  const itemRef = useRef<HTMLLIElement | null>(null);

  const observerCallback = (entries: any): void => {
    if (isArrowed && itemRef?.current && entries[0].intersectionRatio < 0.90) {
      // if less than 90% of item is showing, it will scroll to display the item
      itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
    }
  };

  useEffect(() => {
    if (itemRef?.current) {
      const observer = new IntersectionObserver(observerCallback);
      observer.observe(itemRef.current);
      return (): void => { observer.disconnect(); };
    }
    return undefined;
  }, [isArrowed, itemRef]); // eslint-disable-line react-hooks/exhaustive-deps

  const onKeyPressHandler = keyPressHelper(onChoose);

  if (!isDisplayed) return null;

  return (
    <li
      {...liProps}
      ref={itemRef}
      aria-selected={isArrowed}
      className={classnames(
        `bf-${baseName}__${variantName}`, // base-variant class for post consumption styling
        styleClassNames[variantName], // class for css modules
        className, // user specified class
        { [styleClassNames[`${variantName}-warning`]]: isWarningItem },
        { [styleClassNames.selected]: isSelected },
        { [styleClassNames.arrowed]: isArrowed },
        { [styleClassNames['not-selectable']]: isDisabled },
      )}
      id={id || name?.toString()}
      onClick={(e: SyntheticEvent): void => {
        if (onChoose) {
          onChoose(name, e);
        }
      }}
      onKeyPress={(e: KeyboardEvent<HTMLLIElement>): void => {
        onKeyPressHandler(e, name);
      }}
      role="option"
      tabIndex={isTabbable ? 0 : undefined}
    >
      {icon && (
        <span
          className={`${icon} ${styleClassNames.icon}`}
          style={iconSize ? { fontSize: `${iconSize}px` } : undefined}
        />
      )}
      {children}
    </li>
  );
};

export default BaseDropdownItem;
